import { useEffect } from "react";
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  Hourglass,
  Link2Off,
  RefreshCw,
} from "lucide-react";
import { useCheckout } from "../context/CheckoutContext";
import { normalizeUrlProtocol } from "../utils/redirectUrlBuilder";

const merchantLabel = (merchantName) => merchantName?.trim() || "the merchant";

const TYPE_META = {
  not_found: {
    icon: Link2Off,
    color: "#64748b",
    tint: "#f1f5f9",
  },
  expired: {
    icon: Clock,
    color: "#b45309",
    tint: "#fef3c7",
  },
  rate_limited: {
    icon: Hourglass,
    color: "#1d4ed8",
    tint: "#dbeafe",
  },
  unavailable: {
    icon: AlertTriangle,
    color: "#b91c1c",
    tint: "#fee2e2",
  },
  error: {
    icon: AlertCircle,
    color: "#b91c1c",
    tint: "#fee2e2",
  },
};

const buildContent = (merchantName, message) => ({
  not_found: {
    heading: "Checkout unavailable",
    sub: "This checkout link isn't valid or no longer exists.",
    hint: `Return to ${merchantLabel(merchantName)} and start checkout again from their website.`,
  },
  expired: {
    heading: "Checkout link expired",
    sub: "This checkout session has timed out for your security.",
    hint: `Return to ${merchantLabel(merchantName)} and start a new checkout from their website.`,
  },
  rate_limited: {
    heading: "Please wait a moment",
    sub: "Too many checkout attempts from your connection.",
    hint: "Wait about a minute, refresh this page, and try again.",
  },
  // A merchant-side configuration problem (e.g. no payment method set up for
  // this currency) rather than a missing/expired link — the backend message
  // is specific and safe to show, so surface it instead of generic copy.
  unavailable: {
    heading: "Checkout unavailable",
    sub: message || "This checkout can't be completed right now.",
    hint: `Please contact ${merchantLabel(merchantName)} so they can resolve this.`,
  },
  error: {
    heading: "Checkout unavailable",
    sub: "We couldn't open this checkout page right now.",
    hint: `Return to ${merchantLabel(merchantName)} and try again from their website.`,
  },
});

const getReturnUrl = (returnUrl) => {
  if (!returnUrl) return null;
  try {
    const normalized = normalizeUrlProtocol(returnUrl);
    const parsed = new URL(normalized);
    return parsed.origin;
  } catch {
    return normalizeUrlProtocol(returnUrl);
  }
};

const HostedPageError = ({
  errorType = "error",
  message,
  merchantName: merchantNameProp,
  logoUrl: logoUrlProp,
  returnUrl,
}) => {
  const { merchantName: ctxMerchantName, logoUrl: ctxLogoUrl } = useCheckout();
  const merchantName = merchantNameProp || ctxMerchantName;
  const logoUrl = logoUrlProp || ctxLogoUrl;

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes hpeFadeIn { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  const content =
    buildContent(merchantName, message)[errorType] ||
    buildContent(merchantName, message).error;
  const meta = TYPE_META[errorType] || TYPE_META.error;
  const Icon = meta.icon;
  const returnHref = getReturnUrl(returnUrl);
  const returnLabel = merchantName?.trim()
    ? `Return to ${merchantName.trim()}`
    : "Return to store";

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, borderTop: `4px solid ${meta.color}` }}>
        {logoUrl ? (
          <img src={logoUrl} alt={merchantName || "Store logo"} style={styles.logo} />
        ) : (
          <div style={{ ...styles.iconWrap, backgroundColor: meta.tint }}>
            <Icon size={26} color={meta.color} strokeWidth={1.9} />
          </div>
        )}

        <h1 style={styles.heading}>{content.heading}</h1>
        <p style={styles.sub}>{content.sub}</p>

        <div style={styles.hintBox}>
          <p style={styles.hint}>{content.hint}</p>
        </div>

        <div style={styles.actions}>
          {returnHref && (
            <a href={returnHref} style={styles.buttonPrimary}>
              {returnLabel}
            </a>
          )}
          <button type="button" onClick={handleRefresh} style={styles.buttonSecondary}>
            <RefreshCw size={15} strokeWidth={2.2} aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif",
    padding: "24px",
  },
  card: {
    position: "relative",
    maxWidth: "420px",
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
    padding: "40px 36px 36px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    animation: "hpeFadeIn 0.3s ease-out",
  },
  logo: {
    maxHeight: "40px",
    maxWidth: "180px",
    objectFit: "contain",
    marginBottom: "4px",
  },
  iconWrap: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "4px",
  },
  heading: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#0f172a",
    margin: "0",
    letterSpacing: "-0.3px",
  },
  sub: {
    fontSize: "14px",
    color: "#475569",
    margin: "0",
    lineHeight: "1.6",
  },
  hintBox: {
    width: "100%",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "10px 14px",
    boxSizing: "border-box",
  },
  hint: {
    fontSize: "13px",
    color: "#64748b",
    margin: "0",
    lineHeight: "1.6",
  },
  actions: {
    marginTop: "8px",
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "10px",
    alignItems: "stretch",
  },
  buttonPrimary: {
    flex: "1 1 0",
    padding: "11px 12px",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "500",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    textAlign: "center",
  },
  buttonSecondary: {
    flex: "1 1 0",
    padding: "11px 12px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
};

export default HostedPageError;
